import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { OrnamentDivider } from "@/components/Floral";

const PersonChoice = ({ name, value, onChange, testPrefix }) => (
  <div className="text-center">
    <p className="font-script text-4xl mb-1" style={{ color: "var(--ink)" }}>
      {name}
    </p>
    <p className="font-serif-d italic text-base mb-5" style={{ color: "var(--ink-soft)" }}>
      Kommer du att fira med oss?
    </p>
    <div className="flex justify-center gap-3">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`btn-invite ${value === true ? "btn-solid" : ""}`}
        data-testid={`${testPrefix}-yes-btn`}
      >
        Ja, jag kommer
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`btn-invite ${value === false ? "btn-solid" : ""}`}
        data-testid={`${testPrefix}-no-btn`}
      >
        Tyvärr inte
      </button>
    </div>
  </div>
);

export const Rsvp = ({ token, guest, settings, onUpdated }) => {
  const answered = guest.rsvp_at != null;
  const [editing, setEditing] = useState(!answered);
  const [attending, setAttending] = useState(guest.attending);
  const [plusOne, setPlusOne] = useState(guest.plus_one_attending);
  const [dietary, setDietary] = useState(guest.dietary || "");
  const [allergies, setAllergies] = useState(guest.allergies || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const hasPlusOne = Boolean(guest.plus_one_name);
  const ready = attending !== null && attending !== undefined && (!hasPlusOne || (plusOne !== null && plusOne !== undefined));
  const anyoneComing = attending === true || plusOne === true;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invite/${token}/rsvp`, {
        attending,
        plus_one_attending: hasPlusOne ? plusOne : null,
        dietary,
        allergies,
      });
      onUpdated(data);
      setDone(true);
      setEditing(false);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!editing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="text-center"
        data-testid="rsvp-confirmation"
      >
        {done || anyoneComing ? (
          <>
            <p className="font-script text-4xl sm:text-5xl leading-snug" style={{ color: "var(--ink)" }}>
              Vi längtar efter att fira med er.
            </p>
            <p className="font-serif-d italic text-lg mt-6" style={{ color: "var(--ink-soft)" }}>
              Ert svar har tagits emot.
            </p>
          </>
        ) : (
          <>
            <p className="font-script text-4xl sm:text-5xl leading-snug" style={{ color: "var(--ink)" }}>
              Ni kommer att vara saknade.
            </p>
            <p className="font-serif-d italic text-lg mt-6" style={{ color: "var(--ink-soft)" }}>
              Ert svar har tagits emot.
            </p>
          </>
        )}
        <div className="mt-8 space-y-1 font-serif-d text-lg" style={{ color: "var(--ink)" }}>
          <p>
            {guest.name} — {guest.attending ? "Kommer" : "Kommer inte"}
          </p>
          {hasPlusOne && (
            <p>
              {guest.plus_one_name} — {guest.plus_one_attending ? "Kommer" : "Kommer inte"}
            </p>
          )}
        </div>
        <OrnamentDivider className="my-8" />
        <p className="font-script text-3xl" style={{ color: "var(--gold)" }}>
          {settings?.couple_name_1} &amp; {settings?.couple_name_2}
        </p>
        <button className="btn-invite mt-10 text-xs" onClick={() => setEditing(true)} data-testid="rsvp-edit-btn">
          Ändra svar
        </button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-12">
      <PersonChoice name={guest.name} value={attending} onChange={setAttending} testPrefix="rsvp-guest" />
      {hasPlusOne && (
        <>
          <OrnamentDivider />
          <PersonChoice name={guest.plus_one_name} value={plusOne} onChange={setPlusOne} testPrefix="rsvp-plusone" />
        </>
      )}

      <AnimatePresence>
        {ready && anyoneComing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="max-w-md mx-auto space-y-8 pt-2">
              <div>
                <p className="label-caps text-xs mb-4" style={{ color: "var(--ink-soft)" }}>
                  Kostpreferenser
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {["Inga särskilda önskemål", "Vegetariskt", "Halalkött", "Annat"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setDietary(opt)}
                      className={`btn-invite text-[0.65rem] px-4 py-2.5 ${dietary === opt ? "btn-solid" : ""}`}
                      data-testid={`dietary-${opt === "Vegetariskt" ? "veg" : opt === "Halalkött" ? "halal" : opt === "Annat" ? "other" : "none"}-btn`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="label-caps text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
                  Allergier eller annat vi bör veta
                </p>
                <input
                  className="input-line text-center"
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  placeholder="Valfritt"
                  data-testid="allergies-input"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {ready && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <button className="btn-invite btn-solid" onClick={submit} disabled={submitting} data-testid="rsvp-submit-btn">
              {submitting ? "Skickar…" : "Bekräfta OSA"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
